import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  asArray,
  sameRefList,
  isFiledSelfOf,
  layoutCurrentDesigns,
  resolveActiveConfigId,
} from '@repo/front-end-utils';
import { useFloorplanStore } from '@repo/front-end-state';
import {
  getConfigurationStatementOutcome,
  getRTSPEndpointsOrThrow,
  getTwinSenseConfigsOutcome,
} from '@repo/front-end-services';
import type {
  ConfigurationStatement,
  FloorPlannerComposite,
  FloorPlannerLayout,
  RTSPEndpoint,
  StatementObserver,
} from '@repo/frontend-types';

import Config from '../../common/Config';
import { LIVE_OBSERVER_MARKER, matchObserversToPlan } from './matchObserversToPlan';
import type { MatchObserversResult } from './matchObserversToPlan';
import {
  LIVE_COMPOSITE_MARKER,
  liveCompositeRefid,
  matchCompositesToPlan,
} from './matchCompositesToPlan';
import { encodeRef } from './compositeRefOptions';
import type { MatchCompositesResult } from './matchCompositesToPlan';
import { fetchLiveTwinLayout } from './liveTwinLayout';
import { liveEntityRefids } from './sessionRecords';
import type { FirstMatchSource } from './draftChoice';
import { hasUnlaunchedEdits } from './draftChoice';

/**
 * Read what a twin is measuring right now, and put it into the editing session.
 *
 * Sixteen production gyms carry camera streams and ten have a live observer configuration — 2,271
 * observers between them — and until this hook not one of them reached the wizard. So the only
 * screen for editing a twin could not see the thing it was editing.
 *
 * ## Four reads, four separate outcomes
 *
 * Streams, the active configuration, its **statement** — and, since `camera-observer-setup-attention`,
 * the twin's running model as a plan, the reference geometry for "has this moved since its observer
 * was drawn". Each read answers for itself, and **failed is never folded into empty**.
 *
 * The third read used to be the observers alone. Since
 * `complex-measures-in-configuration-statement-read` it is the whole configuration as a statement
 * about entities — the observers AND the complex measures — with the polygon and track-side parsing
 * done server-side. `observers` is still exposed, derived from the statement, because four
 * consumers read it and none of them needs to change for this. That is the whole point
 * rather than a nicety: a stage that renders a broken read and a twin with no observers identically
 * tells an operator their gym measures nothing, and the next thing they do is press Go Live. The
 * shape follows `useLiveTwinPlan` in this folder, which states the same reasoning for the same
 * reason.
 *
 * ## It is mounted on the page, not on the stage
 *
 * FR9 requires the load on every entry route — a resumed plan, *Start from this twin*, and a
 * live-model import mid-session. The Observers stage only mounts when the operator reaches step 5,
 * so a hook living there would serve one route of three. Ten of the sixteen production twins with
 * streams have no saved plan at all.
 *
 * ## What it writes, and what it must not
 *
 * Only the `observers` array of each floor's latest design, through the store's existing
 * `setAllFloorObservers`. Areas, surfaces, items and cameras are not its business (FR8/AC25). It
 * writes nothing to the twin: every call here is a read, and Go Live remains the only writer (FR11).
 */

/** One read's answer. `failed` carries a sentence a person can act on, never an error code. */
export type ReadOutcome<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'failed'; reason: string };

/** The configuration read has a fourth answer: the twin has never had one. */
export type ConfigOutcome =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'ready'; id: number; activeIds: number[] }
  | { status: 'failed'; reason: string };

/**
 * The RUNNING MODEL's fourth answer, like the configuration's: a twin that has never been taken
 * live has no model, and that is not a failure (`camera-observer-setup-attention`).
 */
export type ModelOutcome =
  | { status: 'loading' }
  | { status: 'none' }
  | {
      status: 'ready';
      value: FloorPlannerLayout;
      /** Entities the running model holds with no identity, which were not compared. */
      skipped: number;
    }
  | { status: 'failed'; reason: string };

/**
 * Which set of observers the plan holds after the first match (`observers-page-draft`, FR9/FR10;
 * decided by the page since `twin-launch-history-draft-choice`).
 *
 * - `running` — the running configuration's observers replaced the plan's: a plan with no unlaunched
 *   edits, or one the person chose to replace with production.
 * - `draft` — the plan holds unlaunched work and was kept. The notice says so.
 * - `none` — the twin runs nothing, so there was nothing to replace the plan's observers with.
 *
 * `null` until a match has run.
 */
export type ObserverSource = 'running' | 'draft' | 'none';

export interface LiveTwinConfiguration {
  streams: ReadOutcome<RTSPEndpoint[]>;
  /** The twin `streams` was read for. Compare with the current twin before pairing the two. */
  streamsTwinId: number | null;
  config: ConfigOutcome;
  /**
   * The whole configuration as a statement about entities. The read that actually happens.
   *
   * ⚠ **Nothing on screen renders the raw statement yet** — every surface goes through `observers`
   * and `compositeMatch`, which this hook derives from it below. The statement itself is live and
   * load-bearing; it is exposed under its own name because `composites-stage` and
   * `coverage-checklist` need the whole thing, and said here so the next reader can tell a field no
   * UI has reached yet from one nothing uses at all. It is not dead, and removing it would take the
   * composite placement with it.
   */
  statement: ReadOutcome<ConfigurationStatement>;
  /**
   * The statement's observers, kept under this name for the four consumers that read it
   * (`OnboardingObservers`, `OnboardingCompositeMeasures`, `LiveConfigurationNotices`, and the
   * page). Derived — there is no second read behind it.
   */
  observers: ReadOutcome<StatementObserver[]>;
  /**
   * The twin's running model as a plan — the reference geometry for "has this entity moved since
   * its observer was drawn" (`camera-observer-setup-attention`, spec FR3/FR5). Independent of the
   * other three reads: it neither delays nor fails them.
   */
  model: ModelOutcome;
  /** What the last match produced — the counts and the identifiers the stage reports (FR4, FR6). */
  match: MatchObserversResult | null;
  /** What the last COMPOSITE match produced. `null` until one has run. */
  compositeMatch: MatchCompositesResult | null;
  /** Increments on every successful load, so the Observers stage re-hydrates. */
  loadNonce: number;
  /**
   * Increments when a clean re-match changed the set of composite refids on the plan — the page's
   * signal to persist, so the saved document does not keep ids the configuration no longer holds
   * (ledger row 164). A signal only: this hook performs no write.
   */
  refidsChangedNonce: number;
  /**
   * The configuration the last signalling re-match was computed from. The page saves only when
   * this is the configuration its own Go Live just filed: a different id alone does not prove the
   * re-read came from this page's filing — another session may have activated one.
   */
  refidsChangedConfigId: number | null;
  /** Re-runs one read. Each failure banner's Retry calls its own (FR13). */
  retry: (which: 'streams' | 'config' | 'observers' | 'model') => void;
  /** Which observers the plan holds after the match (`ObserverSource`). `null` until one has run. */
  observerSource: ObserverSource | null;
  /**
   * Forget the match history, so the next match is a FIRST match again — decided by
   * `firstMatchObserverSource`. The page calls it before re-hydrating a plan it has swapped in
   * (*Discard Draft*, *Load as Draft*, *Restore dropped draft*): a swapped plan is a new
   * session's plan, and a re-match would carry the old one's observers across.
   */
  restartMatch: () => void;
}

export interface UseLiveTwinConfigurationParams {
  /** Numeric twin id from `?twinId=`. */
  twinId: number | undefined;
  /** Twin BID from `?twinBid=` — the prefix every identity folds through. */
  twinBid: string | undefined;
  accessToken: string | undefined;
  /** The planner session id; the store's layout belongs to this feature only when they agree. */
  sessionId: string;
  /**
   * The org the OPERATOR has selected (`authMetadata.orgBID`) — an input to the running-model
   * read's org check, never the answer. Absent, that read reports `failed` with the same sentence
   * the Floor Plan chooser shows, because nothing in the app supplies an org without a user action.
   */
  selectedOrgBid?: string;
  /**
   * Off when the wizard has refused the address.
   *
   * ⚠ The page carries a cross-twin refusal — `?twinId=` and `?twinBid=` naming different twins —
   * and BOTH of its other reads honour it: the register read returns early, the plan read returns
   * early and carries it as a dependency, and a dedicated effect resets the stores so one
   * customer's data does not sit behind a screen saying the wizard refused. These three reads were
   * the only ones on the page that did not, so they fetched the mismatched twin's streams,
   * configurations and observers anyway. Nothing survived — the reset nulls the layout, so the
   * match returns early — but that containment was incidental rather than expressed, and the
   * residue argument that effect was written for applies to these reads identically.
   */
  enabled?: boolean;
  /**
   * Which observers the FIRST match keeps (`twin-launch-history-draft-choice`, R2.4) — decided by
   * the page before it hydrates the plan (`draftChoice.ts`), never inferred here from a marker.
   * `'draft'` keeps the plan's; `'running'` takes the running configuration's. **`null` holds the
   * first match** until the page has decided. A twin running nothing keeps the plan's whatever this
   * says (rule (a)). Re-matches do not read it.
   *
   * ⚠ **A getter, read when the match runs — not a value captured at render.** The page decides and
   * then loads the plan into the zustand store in one synchronous step, and a store update can
   * render before a React state update lands, so a captured value could be the PREVIOUS decision
   * for one render — exactly the render the first match runs in. The getter reads the page's ref,
   * which is set before the store is.
   */
  firstMatchObserverSource: () => FirstMatchSource | null;
}

/*
 * ⚠ **There is deliberately no `?configId=` override here**, and that is a narrowing of this
 * feature's own spec, decided 2026-09-10 against a ruling that post-dates it.
 *
 * The spec said the override should be carried, matching the two existing stages. Feature 03 then
 * removed `?configId=` as a WRITE override (its decision D3) and left it alive only as a frame-capture
 * correlation tag — "only an MQTT correlation tag for frame capture and writes nothing". The Observers
 * stage is the one left reading it (Camera Feeds' copy went with `camera-feeds-page-panel`).
 *
 * Honouring it here would quietly undo that. This read decides which observers an operator sees as
 * their twin's live configuration, and Save then persists them into the plan — so a hand-typed URL
 * parameter would once again reach the twin's measured state, by a longer route. FR2 asks for the
 * **active** configuration; that is what this resolves, always.
 */

/**
 * Log a failed read narrowly.
 *
 * Narrow fields only — an AxiosError carries the caller's `Authorization: Bearer …` header, and
 * `console.error(msg, error)` renders it expandable in a console that travels in screenshots and
 * pasted tickets. Same shape as `getTwinSenseConfigsOutcome`'s own handler.
 */
const reportReadFailure = (what: string, id: number | undefined, err: unknown): void => {
  const e = err as { response?: { status?: number }; message?: string } | null;
  console.error(
    `[live-config] could not read ${what}:`,
    id,
    e?.response?.status ?? '(no status)',
    e?.message ?? String(err),
  );
};

const REASON = {
  streams: "Could not read this twin's camera streams.",
  config: "Could not read this twin's observer configuration.",
  observers: "Could not read this twin's configuration statement.",
  model: 'Could not read the running twin.',
  noTwin: 'No twin is named in the address, so its model cannot be read.',
  noOrgSelected:
    'No organisation is selected, so this twin’s model cannot be read. Open the twin first, then return here.',
} as const;

/**
 * How many live entities this plan's CURRENT designs carry — the signal the re-match guard watches.
 *
 * ⚠ Three things here were wrong and each was silent. It walked EVERY design on a floor rather than
 * the current one, so a superseded version moved the count; it used `?? []`, which `asArray` exists
 * because of — a layout column holding `{designs: {}}` survives the nullish check and then throws
 * inside this effect, taking the wizard down rather than reporting a read that failed; and it
 * counted soft-deleted entities the matcher filters out, so a plan whose only change was a deletion
 * moved a number that is supposed to mean "something new arrived to place".
 */
const planIdentityCount = (layout: FloorPlannerLayout | null): number =>
  liveEntityRefids(layout).length;

/**
 * Whether two stored composites say the same thing: target and members by their canonical encoding
 * (in order — order is part of a member list), and the two flags read as booleans, because absent
 * and `false` mean the same thing on the plan. `generated` is not part of what a total SAYS — the
 * plan carries it and the configuration never does — so it is not compared.
 */
const sameCompositeDefinition = (a: FloorPlannerComposite, b: FloorPlannerComposite): boolean =>
  (a.target ? encodeRef(a.target) : '') === (b.target ? encodeRef(b.target) : '') &&
  // Element by element, never by joining encodings: a refid is opaque, so no delimiter is safe.
  sameRefList(asArray(a.inputs), asArray(b.inputs)) &&
  (a.includesOwnObservers === true) === (b.includesOwnObservers === true) &&
  (a.excluded === true) === (b.excluded === true);

/**
 * Whether the merge changed which refids the plan holds. Order and content are not compared.
 *
 * `before` is the raw JSON column, so it is guarded exactly as `carryOverAcrossRematch` guards its
 * input; `after` is the merge's own output and is well-formed by construction.
 */
const refidsChanged = (before: { refid?: unknown }[], after: { refid: string }[]): boolean => {
  const was = new Set(
    before.filter(c => c != null && typeof c.refid === 'string').map(c => c.refid as string),
  );
  const now = new Set(after.map(c => c.refid));
  return was.size !== now.size || after.some(c => !was.has(c.refid));
};

/**
 * Merge what the loaded configuration says with what the operator has done to it.
 *
 * ## The rule, for a caller that can name an entry's IDENTITY
 *
 * 1. **The operator's saved work wins.** A measure they drew, edited or deleted and SAVED is in
 *    the plan document, and the plan is what Go Live files — so on a first open the plan's copy
 *    stands and the configuration's version of the same measure is suppressed. A tombstone
 *    suppresses its loaded twin outright.
 * 2. **A drawn measure is superseded once the configuration holds its identity.** That is a
 *    measure Go Live has since filed, coming back as its own filed self rather than sitting beside
 *    it. For a measure the identity is what it TOTALS — a target carries exactly one measure,
 *    which is the stage's own `duplicate-target` rule. This applies on a re-match too, because a
 *    re-match runs straight after Go Live.
 * 3. **A RE-match replaces the loaded entries it placed itself.** Those are the previous match's
 *    output, not saved work, and a re-match happens because new data arrived.
 * 4. **A saved copy suppresses the configuration's copy by identity, not only by refid.** Every
 *    filing mints new composite ids, so the plan's saved `cmp:live-90` and the configuration's
 *    `cmp:live-296` can be the same measure (staging `PUL-CON`, 2026-09-30: two whole-gym totals on
 *    every open). The saved copy's content stands and it takes the loaded refid — but only when its
 *    own refid is STALE, so a later delete tombstones an id the configuration still holds. A saved
 *    copy whose refid is still loaded was edited onto this target and keeps it. Where the plan
 *    already holds two saved copies of one measure, one is re-keyed and the rest are left as saved:
 *    choosing between them is the operator's, and Launch Twin names the duplicate.
 *
 *    **Tombstones stay refid-only.** A tombstone records that the operator deleted one filed
 *    measure, by its id. Suppressing by its identity would also hide a measure the configuration
 *    holds on that target under a DIFFERENT id — one filed after the deletion, which the plan makes
 *    no claim about — so it would vanish from the wizard while still running.
 *
 * ## Why rule 1 exists, and what it cost to find
 *
 * ⚠ The rule used to be "live wins on every open", full stop — right for an OBSERVER, which is
 * drawn and taken live inside one session, and destructive here. `statement-read` put measures on
 * the plan and nothing could add one, so it had no victim; this stage can. On a twin that already
 * has a live configuration — most of the estate — a measure the operator added, edited or deleted
 * and saved was replaced on the next open **while staying in the plan document**: invisible,
 * undeletable, and still there for Go Live to file. The add half was measured on staging plan 51
 * by driving the real app; the edit and delete halves were found by review against this spec's own
 * acceptance criterion 11, which requires all three to survive Save and a reload.
 *
 * ## Observers pass no identity
 *
 * So whenever the hook CALLS this for observers on a first match, the loaded set replaces the
 * plan's wholesale. Whether it calls it at all is decided before, by the observer source
 * (`observers-page-draft`, FR9): a twin running nothing, or a plan whose saved draft is current,
 * keeps its own observers and this is never reached for them. The observer side's own first-match
 * tombstone hole is pre-existing and is recorded rather than fixed here.
 */
/**
 * Exported for its unit tests only; the hook is its one caller.
 */
export const carryOverAcrossRematch = <T extends { refid: string; _deleted?: boolean }>(
  existing: T[],
  loaded: T[],
  marker: string,
  isReMatch: boolean,
  identityOf?: (entry: T) => string,
  /**
   * Every refid the configuration HOLDS, placed or not. A saved edit is stale only when its refid is
   * in none of them: a measure the configuration still has but could not place on this plan (a
   * member issue) is not gone, and re-keying away from it would free it to come back. Defaults to
   * the placed set.
   */
  heldRefids?: ReadonlySet<string>,
  /**
   * What a loaded entry INHERITS from the entry it takes the place of, by identity
   * (`complex-measures-in-configuration-parent-space-composites`, FR9). The plan carries facts the
   * configuration does not — a generated total's `generated` flag never reaches the middleware — so
   * when the configuration's copy replaces the plan's, the fact would otherwise be lost. Two
   * replacements, and both inherit:
   *
   * - **Rule 2** — a DRAWN entry superseded by the configuration's copy of the same identity: a
   *   generated total Go Live has just filed, coming back as its filed self.
   * - **Rule 3** — on a RE-match, the previous match's loaded entry replaced by the new one: the same
   *   generated total at the NEXT Go Live, under a new id. Without this the flag would survive one
   *   Go Live and be lost at the second.
   *
   * Saved edits (rule 1) keep their own object and need nothing. Tombstones donate nothing.
   */
  inherit?: (from: T, onto: T) => T,
  /**
   * On a RE-match, which of the plan's loaded entries are the operator's saved work rather than the
   * previous match's output (`composites-page-draft`). Without it a re-match treats every loaded
   * entry as its own output and replaces it — so a re-match against the SAME configuration (a
   * streams Retry) put the configuration's copy back over an edit the operator had made and saved,
   * and the next save of any kind, the draft's included, sent that revert to the server. The caller
   * answers it by comparison with what the configuration itself said last time; an entry it marks
   * is kept exactly as a first match keeps a saved edit (rule 1). Absent, a re-match keeps none.
   */
  isSavedOnReMatch?: (entry: T) => boolean,
  /**
   * When the plan's DRAFT was taken (`twin-launch-history-draft-choice`: an edited draft opened as
   * saved, launched twin or not, or one restored), rule 2 asks one more question before a drawn
   * entry is superseded: is the configuration's copy really ITS filed self? Same target is not
   * enough — a stale draft can hold a drawn total on a target that someone ELSE has since filed a
   * different total on, and superseding it then swaps production's total in without asking: a
   * silent replacement of the draft opened as saved (only *Discard Draft* replaces it). Absent
   * (every other match), rule 2 supersedes by identity alone, as before. When it answers no, both stay, and Launch's
   * duplicate-target rule names the clash for the person to resolve.
   */
  isFiledSelf?: (drawn: T, superseder: T) => boolean,
): T[] => {
  // Observers on a first match (or a re-match against a new configuration — the hook passes
  // `false` for that too): the loaded set replaces the plan's wholesale.
  if (!isReMatch && !identityOf) return loaded;

  /*
   * Guarded here rather than with `isLive`, and both halves matter. A layout read off a JSON
   * column guarantees nothing about its shape, so an entry can be nullish or carry no `refid` —
   * the reason `asArray` exists. And `isLive` is a type PREDICATE, so using it negated narrows the
   * entry to `never` and the tombstone test cannot read `refid` at all.
   */
  const wellFormed = existing.filter(
    (entry): entry is T => entry != null && typeof entry.refid === 'string',
  );

  const drawn = wellFormed.filter(e => !e.refid.includes(marker) && e._deleted !== true);
  const tombstones = wellFormed.filter(e => e.refid.includes(marker) && e._deleted === true);
  /*
   * The plan's own saved copy of a LOADED entry — an edit the operator made and saved.
   *
   * Only on a first match, and only for a caller that supplied an identity. On a RE-match these
   * same entries are the previous match's output rather than saved work, and a re-match happens
   * BECAUSE new data arrived, so replacing them is the point.
   */
  const savedEdits = identityOf
    ? wellFormed.filter(
        e =>
          e.refid.includes(marker) &&
          e._deleted !== true &&
          (!isReMatch || (isSavedOnReMatch?.(e) ?? false)),
      )
    : [];

  const supersededRefids = new Set(tombstones.concat(savedEdits).map(e => e.refid));

  /*
   * Rule 4 — a saved copy suppresses the configuration's copy of the SAME MEASURE, whatever id the
   * configuration now gives it. Tombstones are deliberately not in this map; see the header.
   *
   * `''` is skipped on both sides: it is the identity of a measure with no target, and two of those
   * are not the same measure.
   */
  const savedByIdentity = new Map<string, T[]>();
  if (identityOf) {
    for (const saved of savedEdits) {
      const identity = identityOf(saved);
      if (identity === '') continue;
      savedByIdentity.set(identity, (savedByIdentity.get(identity) ?? []).concat(saved));
    }
  }
  const loadedRefids = heldRefids ?? new Set(loaded.map(e => e.refid));
  /** Saved edit → the loaded refid it takes over. At most one each way. */
  const rekeyed = new Map<T, string>();

  const survivingLoaded = loaded.filter(e => {
    if (supersededRefids.has(e.refid)) return false;
    const identity = identityOf ? identityOf(e) : '';
    const sameMeasure = identity === '' ? undefined : savedByIdentity.get(identity);
    if (!sameMeasure) return true;
    /*
     * Re-key only a saved edit whose own refid is STALE — the configuration no longer holds it.
     * One whose refid is still loaded was edited onto this target from another measure, and taking
     * the new id would free the old one to come back on its original target at the next open.
     */
    const stale = sameMeasure.find(s => !loadedRefids.has(s.refid) && !rekeyed.has(s));
    if (stale) rekeyed.set(stale, e.refid);
    return false;
  });
  const savedEditsOut = savedEdits.map(s => {
    const refid = rekeyed.get(s);
    return refid === undefined ? s : { ...s, refid };
  });

  /*
   * A drawn entry whose identity the configuration now holds is superseded by it — that is a
   * measure the operator drew and Go Live has since filed, coming back as its own filed self
   * rather than sitting beside it and growing by one on every trip.
   *
   * On BOTH branches: a re-match runs straight after Go Live (`retry('config')`), which is exactly
   * when a drawn measure meets its filed twin.
   */
  const keptByIdentity = new Map<string, T[]>();
  if (identityOf) {
    for (const entry of savedEditsOut.concat(survivingLoaded)) {
      const identity = identityOf(entry);
      // `''` is no identity — a target-less measure supersedes nothing (rule 4 skips it too).
      if (identity === '') continue;
      keptByIdentity.set(identity, (keptByIdentity.get(identity) ?? []).concat(entry));
    }
  }
  const isSuperseded = (e: T): boolean => {
    const holders = identityOf ? keptByIdentity.get(identityOf(e)) : undefined;
    if (!holders) return false;
    return isFiledSelf ? holders.some(h => isFiledSelf(e, h)) : true;
  };
  const survivingDrawn = identityOf ? drawn.filter(e => !isSuperseded(e)) : drawn;

  let loadedOut = survivingLoaded;
  if (inherit && identityOf) {
    const donors = drawn
      .filter(isSuperseded)
      .concat(
        isReMatch ? wellFormed.filter(e => e.refid.includes(marker) && e._deleted !== true) : [],
      );
    const donorByIdentity = new Map<string, T>();
    for (const donor of donors) {
      const identity = identityOf(donor);
      if (identity !== '' && !donorByIdentity.has(identity)) donorByIdentity.set(identity, donor);
    }
    if (donorByIdentity.size > 0) {
      loadedOut = survivingLoaded.map(e => {
        const donor = donorByIdentity.get(identityOf(e));
        return donor ? inherit(donor, e) : e;
      });
    }
  }

  return survivingDrawn.concat(tombstones, savedEditsOut, loadedOut);
};

export const useLiveTwinConfiguration = ({
  twinId,
  twinBid,
  accessToken,
  sessionId,
  selectedOrgBid,
  enabled = true,
  firstMatchObserverSource,
}: UseLiveTwinConfigurationParams): LiveTwinConfiguration => {
  const [streams, setStreams] = useState<ReadOutcome<RTSPEndpoint[]>>({ status: 'loading' });
  /*
   * Which twin `streams` was read FOR — not which twin is currently selected.
   *
   * ⚠ The two differ for exactly one render, and that render is the dangerous one. The effect below
   * calls `setStreams({ status: 'loading' })`, but an effect runs AFTER commit: on the render where
   * `twinId` changes, `streams` still holds the previous twin's rows while `twinId` is already the
   * new one. A consumer that reads both — and the Observers stage hands them to the camera wall
   * together — pairs one customer's endpoint ids with another customer's twin. Stamping the value
   * lets a consumer ask whether they belong together instead of assuming it.
   *
   * Deliberately NOT a field on `ReadOutcome`, which all three reads share and three files consume.
   */
  const [streamsTwinId, setStreamsTwinId] = useState<number | null>(null);
  const [config, setConfig] = useState<ConfigOutcome>({ status: 'loading' });
  const [statement, setStatement] = useState<ReadOutcome<ConfigurationStatement>>({
    status: 'loading',
  });
  const [model, setModel] = useState<ModelOutcome>({ status: 'loading' });
  const [match, setMatch] = useState<MatchObserversResult | null>(null);
  const [compositeMatch, setCompositeMatch] = useState<MatchCompositesResult | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [refidsChangedNonce, setRefidsChangedNonce] = useState(0);
  const [refidsChangedConfigId, setRefidsChangedConfigId] = useState<number | null>(null);
  const [observerSource, setObserverSource] = useState<ObserverSource | null>(null);
  /** Bumped by `retry`, per read, to re-run one effect without re-running the other three. */
  const [attempt, setAttempt] = useState({ streams: 0, config: 0, observers: 0, model: 0 });

  const retry = useCallback((which: 'streams' | 'config' | 'observers' | 'model') => {
    setAttempt(a => ({ ...a, [which]: a[which] + 1 }));
  }, []);

  const ready = enabled && accessToken != null && twinId != null;

  // ---- 1. Streams -----------------------------------------------------------------------------
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setStreams({ status: 'loading' });
    void (async () => {
      try {
        const value = await getRTSPEndpointsOrThrow(Config.middlewareApi, accessToken!, twinId!);
        if (!cancelled) {
          // Stamped in the same batch as the value, so the two can never disagree about their twin.
          setStreamsTwinId(twinId!);
          setStreams({ status: 'ready', value: value ?? [] });
        }
      } catch (err) {
        if (cancelled) return;
        reportReadFailure('RTSP streams', twinId, err);
        setStreams({ status: 'failed', reason: REASON.streams });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, accessToken, twinId, attempt.streams]);

  // ---- 2. Which configuration is live? --------------------------------------------------------
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setConfig({ status: 'loading' });

    void (async () => {
      let outcome;
      try {
        outcome = await getTwinSenseConfigsOutcome(Config.middlewareApi, accessToken!, twinId!);
      } catch (err) {
        if (cancelled) return;
        reportReadFailure('configurations', twinId, err);
        setConfig({ status: 'failed', reason: REASON.config });
        return;
      }
      if (cancelled) return;
      if (!outcome || outcome.status === 'failed') {
        console.error('[live-config] could not read the twin’s configurations:', twinId);
        setConfig({ status: 'failed', reason: REASON.config });
        return;
      }
      if (outcome.status === 'absent') {
        setConfig({ status: 'none' });
        return;
      }
      const resolved = resolveActiveConfigId(outcome.configs);
      if (!resolved) {
        setConfig({ status: 'none' });
        return;
      }
      if (resolved.activeIds.length > 1) {
        // FR14. Deterministic AND said out loud — the stage renders this, it is not only a log.
        console.warn(
          '[live-config] twin has more than one active configuration:',
          twinId,
          resolved.activeIds,
          '→ using',
          resolved.id,
        );
      }
      setConfig({ status: 'ready', id: resolved.id, activeIds: resolved.activeIds });
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, accessToken, twinId, attempt.config]);

  // ---- 3. That configuration, as a statement ----------------------------------------------------
  const configId = config.status === 'ready' ? config.id : null;
  useEffect(() => {
    if (!ready) return;
    if (config.status === 'none') {
      // A twin that has never been taken live has no configuration, and that is not a failure. An
      // EMPTY statement is the right answer here, not a failed read.
      /*
       * ⚠ `configId` and `versionNumber` are PLACEHOLDERS here and must not be read. No configuration
       * has id 0; this twin has no configuration at all, which is a real answer rather than an empty
       * one. The counts are genuinely zero. When `go-live-statement` starts reading the statement's
       * identity it needs a real absent case rather than these — recorded against the epic.
       */
      setStatement({
        status: 'ready',
        value: {
          twinBid: twinBid ?? '',
          configId: 0,
          versionNumber: 0,
          isActive: false,
          observers: [],
          composites: [],
          issues: [],
          folded: { partialGroups: 0 },
        },
      });
      return;
    }
    if (configId == null) return; // still loading, or the configuration read failed
    let cancelled = false;
    // Cancels the REQUEST, not just the state write — the same shape the running-model effect below
    // uses. This is the page's largest read, so an abandoned one is worth tearing down.
    const controller = new AbortController();
    setStatement({ status: 'loading' });

    void (async () => {
      let outcome;
      try {
        outcome = await getConfigurationStatementOutcome(
          Config.middlewareApi,
          accessToken!,
          twinId!,
          configId,
          controller.signal,
        );
      } catch (err) {
        if (cancelled) return;
        reportReadFailure('the configuration statement', configId, err);
        setStatement({ status: 'failed', reason: REASON.observers });
        return;
      }
      if (cancelled) return;
      /*
       * `found` and `failed`, kept apart — the same reasoning the observers read carried, and the
       * reason this is not coerced to an empty statement: a configuration that exists and holds
       * nothing is a real, different answer from a read that broke, and rendering them identically
       * tells an operator their gym measures nothing. The next thing they do is press Go Live.
       *
       * ⚠ A 404 lands here too, and that is the DEPLOY-SKEW window: a viewer build calling this
       * route against a middleware that has not shipped it yet. It shows the error banner with its
       * Retry and writes nothing to the store, which is the correct behaviour for that window —
       * never an empty plan.
       */
      if (!outcome || outcome.status === 'failed') {
        console.error('[live-config] could not read the statement for configuration', configId);
        setStatement({ status: 'failed', reason: REASON.observers });
        return;
      }
      if (outcome.statement.issues.length > 0) {
        // Logged in full, narrow fields only. These are the things the server could not fold
        // cleanly — a member resolving to nothing, a duplicated member — and the whole point of
        // carrying them is that somebody can see them.
        console.warn(
          '[live-config] the configuration statement reported issues:',
          configId,
          outcome.statement.issues.map(i => ({
            kind: i.kind,
            observerId: i.observerId,
            compositeId: i.compositeId,
            detail: i.detail,
          })),
        );
      }
      setStatement({ status: 'ready', value: outcome.statement });
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [ready, accessToken, twinId, twinBid, configId, config.status, attempt.observers]);

  /**
   * The statement's observers, under the name four consumers already read.
   *
   * Derived rather than read: there is one network call, and this is a projection of its answer, so
   * the two can never disagree about what the twin is measuring.
   */
  const observers = useMemo<ReadOutcome<StatementObserver[]>>(
    () =>
      statement.status === 'ready'
        ? { status: 'ready', value: statement.value.observers }
        : statement,
    [statement],
  );

  // ---- 3b. The running model, as a plan --------------------------------------------------------
  // Separate from the three reads above: it shares nothing with them, and a failure here must not
  // touch a single observer count. `enabled`/token gating is the same; it needs the BID rather than
  // the numeric id because dGraph is keyed on it.
  useEffect(() => {
    if (!enabled || !accessToken) return;
    if (!twinBid) {
      // Terminal for the same reason as the org below: the address is a URL parameter nothing in
      // the app supplies later, so "loading" would be a spinner that never ends.
      setModel({ status: 'failed', reason: REASON.noTwin });
      return;
    }
    if (!selectedOrgBid) {
      // Terminal, not loading — nothing in the app supplies an org without a user action, and a
      // spinner that never ends reads as "this twin has no model". Same reasoning, same sentence,
      // as `useLiveTwinPlan`.
      setModel({ status: 'failed', reason: REASON.noOrgSelected });
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setModel({ status: 'loading' });
    void (async () => {
      let outcome;
      try {
        outcome = await fetchLiveTwinLayout({
          middlewareApi: Config.middlewareApi,
          accessToken,
          twinBid,
          selectedOrgBid,
          signal: controller.signal,
          logTag: 'live-config',
        });
      } catch (err) {
        if (cancelled) return;
        reportReadFailure('the running model', twinId, err);
        setModel({ status: 'failed', reason: REASON.model });
        return;
      }
      if (cancelled || outcome.status === 'cancelled') return;
      if (outcome.status === 'failed') setModel({ status: 'failed', reason: outcome.reason });
      else if (outcome.status === 'none') setModel({ status: 'none' });
      else setModel({ status: 'ready', value: outcome.layout, skipped: outcome.skipped });
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, accessToken, twinBid, twinId, selectedOrgBid, attempt.model]);

  // ---- 4. Match, and write into the session ---------------------------------------------------
  const layout = useFloorplanStore(s => s.layout);
  const storeTwinBID = useFloorplanStore(s => s.twinBID);
  const sessionLayout = layout && storeTwinBID === sessionId ? layout : null;

  /*
   * Re-match when the plan gains entities, and ONLY then.
   *
   * FR9's third route is entities arriving in the plan mid-session (once a live-model import, which
   * Launch no longer offers) with no observers of their own, so an observer that could not be
   * placed a moment ago may be placeable now. Re-matching on every layout change instead would
   * rewrite the observers array on every drag in the planner and throw away in-session drawing.
   */
  const entityCount = useMemo(() => planIdentityCount(sessionLayout), [sessionLayout]);
  /** The source the last match chose — read by the next one, which may only keep a draft or none. */
  const observerSourceRef = useRef<ObserverSource | null>(null);
  /** Read inside the match: rule (a) is decided by the configuration read, not the placeholder statement. */
  const configStatus = config.status;
  const lastMatchedRef = useRef<{
    entityCount: number;
    statementValue: ConfigurationStatement | null;
    streamsValue: unknown;
    /** The configuration's own placed composites at that match — what a loaded entry said. */
    loadedComposites: FloorPlannerComposite[];
  } | null>(null);
  /*
   * Match history belongs to one twin's session. The page changes twin in place, without
   * remounting this hook, and a history carried across would make the new twin's FIRST match a
   * re-match from the old one — bypassing the first-match rules (FR9) for its draft, and treating
   * a configuration id that merely differs as a newly filed one.
   */
  const matchIdentity = `${twinBid ?? ''}\u0000${sessionId}`;
  const matchIdentityRef = useRef(matchIdentity);
  /*
   * The statement on hand at a change of twin is the OLD twin's, for the render or two before the
   * new twin's reads replace it. A first match against it would decide the new twin's draft
   * against another twin's configuration, so no match runs on it.
   */
  const staleStatementRef = useRef<ConfigurationStatement | null>(null);
  if (matchIdentityRef.current !== matchIdentity) {
    matchIdentityRef.current = matchIdentity;
    lastMatchedRef.current = null;
    observerSourceRef.current = null;
    staleStatementRef.current = statement.status === 'ready' ? statement.value : null;
  }

  /*
   * What a match was computed FROM — held as the READ ITSELF, not a description of it.
   *
   * The streams are in it because they supply each observer's `rtspEndpointBid`: without them, a
   * stream read that failed, was retried and succeeded hit the "nothing new to place" guard, so the
   * banner cleared while every loaded observer kept the null link it was matched with — a Retry that
   * reports success and does nothing, which is the failure this whole feature exists to remove, one
   * level down.
   *
   * ⚠ **This used to be a signature built from COUNTS, and counts are not the data.** A retry that
   * came back with the same number of observers, composites and streams but different content — a
   * corrected polygon, a member repointed, an entity remapped between the two reads — produced an
   * identical signature, so the guard returned, the plan kept the placement made from the stale read
   * and the wizard reported the retry as successful. That is the same defect the paragraph above
   * describes, reached by changing the data instead of its length.
   *
   * Object identity is the right test rather than a deep fingerprint: each of these is React state
   * replaced wholesale by a completed read and never mutated in place, so a new object means a new
   * read and an unchanged one means no read has landed since. It costs nothing per render, where
   * hashing 151 observers with their polygons would.
   */
  useEffect(() => {
    if (statement.status !== 'ready' || streams.status === 'loading' || !sessionLayout) return;
    if (statement.value === staleStatementRef.current) return; // the previous twin's read
    // The page has not decided which observers a first match keeps: hold it (R2.4).
    const decidedSource = firstMatchObserverSource();
    if (lastMatchedRef.current === null && decidedSource === null) return;

    const statementValue = statement.value;
    const streamsValue = streams.status === 'ready' ? streams.value : null;

    const previous = lastMatchedRef.current;
    if (
      previous &&
      previous.statementValue === statementValue &&
      previous.streamsValue === streamsValue &&
      previous.entityCount >= entityCount
    ) {
      return; // nothing new to place
    }

    const streamList = streams.status === 'ready' ? streams.value : [];
    // The effect returns above unless the statement is `ready`, so this is the statement itself
    // rather than the derived projection — one source for the observers, the composites and the
    // issues, which cannot then disagree about which read they came from.
    const live = statement.value;
    const result = matchObserversToPlan(
      sessionLayout,
      twinBid,
      live.observers,
      streamList,
      live.issues,
    );
    const compositeResult = matchCompositesToPlan(
      sessionLayout,
      twinBid,
      live.composites,
      live.issues,
    );

    if (result.unmatched.length > 0) {
      console.warn(
        '[live-config] observers naming an entity this plan does not have:',
        result.unmatched,
      );
    }
    if (result.unreadableRegions.length > 0) {
      // The third report channel, and the only one that was counted on screen and never logged —
      // so an operator could see "2 could not be read" with no way to find out which two.
      console.warn(
        '[live-config] observers whose stored region could not be read:',
        result.unreadableRegions,
      );
    }
    if (result.unrecognisedTrackSides.length > 0) {
      console.warn(
        '[live-config] observers carrying a track side this app cannot represent:',
        result.unrecognisedTrackSides,
      );
    }
    if (compositeResult.unplaced.length > 0) {
      // In full, and narrow fields only — the same treatment the three observer channels get, for
      // the same reason: a count on screen with no way to find out WHICH is a dead end.
      console.warn(
        '[live-config] complex measures that could not be placed on this plan:',
        compositeResult.unplaced,
      );
    }

    /*
     * Preserve whatever the operator has drawn this session — on a RE-match, and only then.
     *
     * `setAllFloorObservers` replaces each floor's array wholesale, so a re-match would otherwise
     * discard in-session drawings. A loaded observer is identifiable by `:live-` in its refid, so
     * everything without that marker is carried across.
     *
     * ⚠ **`isReMatch` is the whole correctness of this block, and it was missing.** Whenever the
     * running set is TAKEN (rule (c) below), it replaces the plan's observers rather than merging
     * with them. Without the guard, an observer the operator drew, saved and took live came back
     * as `X:live-<id>` from the configuration while the drawn `X:p1` was preserved beside it — the
     * same region twice, growing by one on every trip through Go Live. Rules (a) and (b) below
     * decide first whether the running set is taken at all.
     *
     * ⚠ **Current designs only.** `layoutCurrentDesigns` is what every other reader in this feature
     * uses, and it is what `setAllFloorObservers` writes back into. Flattening every design on a
     * floor read from versions the store never writes to, so a superseded design's observers were
     * promoted into the current one — and duplicated again on each re-match.
     */
    const store = useFloorplanStore.getState();
    const wasClean = !store.isDirty;
    const isReMatch = lastMatchedRef.current !== null;

    /*
     * ⚠ **Which observers win** (`twin-launch-history-draft-choice`, R2.4). On the FIRST match:
     *
     * - **(a) the twin runs nothing** — the plan's observers are never replaced. The statement is the
     *   no-configuration placeholder, so "replacing" them meant wiping them: a twin that had never
     *   been launched lost every saved observer on every open.
     * - **otherwise, what the page decided** before it hydrated the plan (`draftChoice.ts`): `draft`
     *   keeps the plan's — it holds unlaunched work, or production could not be read — and
     *   `running` takes the configuration's. This replaced the silent third rule that took the
     *   running set over a draft based on an older configuration WITHOUT asking (how the Wakefield
     *   observers were lost): an edited draft now keeps its own, and *Discard Draft* is the way
     *   back to production's.
     *
     * A RE-match against the SAME configuration — a streams Retry, or entities arriving
     * mid-session — keeps the plan's observers under (a), or when the first match kept the draft, or
     * when the plan holds unlaunched work NOW: an observer or composite edit stamps `draftBasis` the
     * moment it is made, before its save, precisely so this sees it. Otherwise its carry-over would
     * replace an edited loaded observer with the configuration's copy, and the draft saver would
     * persist the reverted set. (Not the dirty flag: a live-model import makes the store dirty, and
     * its re-match must still add the observers it made placeable.) A re-match
     * against a NEW configuration — the one Go Live has just filed — takes that configuration
     * wholesale (below).
     */
    const previousSource = observerSourceRef.current;
    const sameConfiguration =
      previous !== null && previous.statementValue?.configId === statementValue.configId;
    // The single definition, without a launch: just the plan's own markers.
    const holdsUnlaunchedWork = hasUnlaunchedEdits(sessionLayout, null, null);
    const source: ObserverSource = !isReMatch
      ? configStatus === 'none'
        ? 'none'
        : (decidedSource ?? 'draft')
      : !sameConfiguration
        ? 'running'
        : previousSource === 'none'
          ? 'none'
          : previousSource === 'draft' || holdsUnlaunchedWork
            ? 'draft'
            : 'running';
    observerSourceRef.current = source;

    if (source === 'running') {
      /*
       * A re-match against a DIFFERENT configuration takes it wholesale, exactly as a first match
       * does. That configuration is the one Go Live has just filed FROM this plan (the wizard is
       * locked while it runs), so the plan's drawn observers are in it under new `:live-` refids;
       * carrying them across as well would hold every one of them twice — and a later edit, saved
       * as the draft, would then keep both on every reopen.
       */
      const carryAsReMatch = isReMatch && sameConfiguration;
      const merged = layoutCurrentDesigns(sessionLayout).map((design, i) =>
        carryOverAcrossRematch(
          asArray(design?.observers),
          result.observersByFloor[i] ?? [],
          LIVE_OBSERVER_MARKER,
          carryAsReMatch,
        ),
      );
      store.setAllFloorObservers(merged);
    }

    /*
     * The composites get the SAME rule, from the same function, for the same reasons — which is
     * the whole point of it being a function rather than two nearly-identical filters.
     */
    const compositesBefore = asArray(sessionLayout.composites);
    const compositesAfter = carryOverAcrossRematch(
      compositesBefore,
      compositeResult.composites,
      LIVE_COMPOSITE_MARKER,
      isReMatch,
      /*
       * What a measure TOTALS is its identity — a target carries exactly one measure.
       *
       * `encodeRef`, not `JSON.stringify`: the canonical string form of a reference, which this
       * feature already uses for the picker, the row labels and the graph rules. A stringify
       * compares key ORDER on a value that round-trips through a JSON column, so it would work
       * only for as long as every producer happened to spell the ref the same way.
       */
      composite => (composite.target ? encodeRef(composite.target) : ''),
      new Set(live.composites.map(c => liveCompositeRefid(c.id))),
      /*
       * A generated parent-space total stays generated when the configuration's copy replaces it
       * (`complex-measures-in-configuration-parent-space-composites`, FR9) — but only when that copy
       * IS its filed self. A hand-made total someone else filed on the same target is not, and
       * must not be handed to the reconciler (`isFiledSelfOf` says how that is told).
       */
      (from, onto) =>
        from.generated === true && isFiledSelfOf(from, onto, sessionLayout)
          ? { ...onto, generated: true as const }
          : onto,
      /*
       * A loaded total the operator has changed is theirs, on a re-match against the SAME
       * configuration (`composites-page-draft`): one that no longer says what the configuration
       * said at the last match, or that it no longer places at all. A re-match against a NEW
       * configuration — the one Go Live has just filed from this plan — still takes it wholesale.
       */
      sameConfiguration && previous
        ? (() => {
            const said = new Map(previous.loadedComposites.map(c => [c.refid, c]));
            return (entry: FloorPlannerComposite) => {
              const was = said.get(entry.refid);
              return !was || !sameCompositeDefinition(entry, was);
            };
          })()
        : undefined,
      /*
       * The draft was chosen, on a first match: a drawn total gives way only to its own filed self,
       * never to a different total someone else filed on its target since (maria@twinlabs.ai,
       * 2026-10-06, from the PR review).
       */
      !isReMatch && source === 'draft'
        ? (drawn, superseder) => isFiledSelfOf(drawn, superseder, sessionLayout)
        : undefined,
    );
    store.setComposites(compositesAfter);

    /*
     * A clean RE-match that changed which refids the plan holds leaves the store and the saved
     * document disagreeing: Go Live saved the plan, filed (minting new ids) and re-read, and this
     * merge put the new ids in the store only. Unsaved, the old ids come back on the next open as
     * the operator's saved work (ledger row 164). The page persists on this signal; the hook still
     * writes nothing.
     *
     * Only a re-match against a DIFFERENT configuration — the one Go Live has just filed. That is
     * the one re-match where the document already holds every composite in the store (Go Live
     * saved it before filing, and filed from it), so the save writes new refids and nothing else.
     * ⚠ Any other re-match — a streams Retry, say — runs rule 3 over the SAME configuration, which
     * replaces the operator's saved edits with the configuration's copies; signalling there would
     * PUT that loss to the server (review of this change, 2026-09-30).
     *
     * Not on a first open: the document holds just what was saved, and a save would promote every
     * loaded measure to saved work on a twin nobody saved. Clean only: a dirty session's own Save
     * carries the new ids, and the leave guard covers leaving without it.
     */
    const refiled =
      previous !== null && previous.statementValue?.configId !== statementValue.configId;
    if (refiled && wasClean && refidsChanged(compositesBefore, compositesAfter)) {
      setRefidsChangedConfigId(statementValue.configId);
      setRefidsChangedNonce(n => n + 1);
    }

    /*
     * A load is not the operator's work, so it must not arm the leave guard (FR12/AC10) — a guard
     * that warns on every open is one operators learn to dismiss. `setAllFloorObservers` and
     * `setComposites` both set the dirty flag unconditionally, so it is put back ONLY where it was
     * already clean: a session with genuine unsaved edits keeps them.
     */
    if (wasClean) useFloorplanStore.getState().markClean();

    // Recorded AFTER the merge, which reads `lastMatchedRef.current` to tell a first match from a
    // re-match. Setting it earlier would make every match look like a re-match.
    lastMatchedRef.current = {
      entityCount,
      statementValue,
      streamsValue,
      loadedComposites: compositeResult.composites,
    };

    setMatch(result);
    setCompositeMatch(compositeResult);
    setObserverSource(source);
    setLoadNonce(n => n + 1);
  }, [
    // `observers` is a useMemo over `statement` and changes only when it does, so it is not a second
    // trigger; the effect body reads `statement.value` rather than the projection, deliberately.
    statement,
    streams,
    sessionLayout,
    twinBid,
    entityCount,
    configId,
    configStatus,
    firstMatchObserverSource,
  ]);

  const restartMatch = useCallback(() => {
    lastMatchedRef.current = null;
    observerSourceRef.current = null;
  }, []);

  return {
    streams,
    streamsTwinId,
    config,
    statement,
    observers,
    model,
    match,
    compositeMatch,
    loadNonce,
    refidsChangedNonce,
    refidsChangedConfigId,
    retry,
    observerSource,
    restartMatch,
  };
};
